// src/pages/ForgotPassword.jsx
import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../supabaseClient";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  function getAuthRedirect() {
    // Supabase will redirect here after user clicks the reset link.
    // This must match a URL allowed in your Supabase Auth settings.
    const base =
      window.location.hostname === "localhost"
        ? "http://localhost:3000"
        : window.location.origin;

    return `${base}/reset-password`;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");

    const value = (email || "").trim().toLowerCase();

    if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setErr("Please enter a valid email.");
      return;
    }

    setSending(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(value, {
        redirectTo: getAuthRedirect(),
      });

      if (error) throw error;

      setMsg("Check your inbox for the password reset link.");
    } catch (e) {
      setErr(e?.message || "Could not send reset email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#FFF7ED] font-sans text-[#333333]">
      <header
        className="border-b border-[#FFE1CF] bg-white"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="flex items-center justify-between px-4 pb-3 pt-3">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-bold text-[#FF914D] transition hover:bg-[#FFF4EC] hover:text-[#E9782F] focus:outline-none focus:ring-4 focus:ring-[#FF914D]/20"
            aria-label="Back to Login"
          >
            <span aria-hidden="true" className="text-2xl leading-none">
              ←
            </span>
            <span className="hidden sm:inline">Back</span>
          </Link>

          <div className="flex h-[66px] w-[66px] items-center justify-center rounded-3xl bg-[#FFF4EC] shadow-sm">
            <img
              src="https://tknzgegeniiojzsascfl.supabase.co/storage/v1/object/public/pictures/Icon2.png"
              alt="Start logo"
              className="h-[54px] w-auto select-none object-contain drop-shadow-sm"
              loading="eager"
              decoding="async"
              draggable="false"
            />
          </div>

          <span className="w-[72px]" aria-hidden="true" />
        </div>

        <div className="px-6 pb-7 text-center">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF914D]">
            Account recovery
          </p>

          <h2 className="mt-2 text-[32px] font-black leading-tight tracking-[-0.03em] text-[#333333] md:text-[38px]">
            Forgot Password
          </h2>

          <p className="mx-auto mt-2 max-w-sm text-sm font-semibold leading-relaxed text-[#757575]">
            Enter your email and we’ll send you a secure link to reset your
            password.
          </p>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-md flex-1 px-6 py-8">
        <form
          onSubmit={handleSubmit}
          className="w-full space-y-6 rounded-[28px] border border-[#FFE1CF] bg-white p-6 shadow-sm"
        >
          {err && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-center text-sm font-bold text-red-700">
              {err}
            </div>
          )}

          {msg && (
            <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-center text-sm font-bold text-green-700">
              {msg}
            </div>
          )}

          <div className="space-y-3">
            <label className="block text-sm font-black uppercase tracking-wider text-[#333333]">
              Email
            </label>

            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full rounded-2xl border border-[#FFE1CF] bg-[#FFF7ED] px-4 py-3 text-sm font-semibold text-[#333333] placeholder:text-[#BCBCBC] outline-none transition focus:border-[#FF914D] focus:bg-white focus:ring-4 focus:ring-[#FF914D]/15"
              autoComplete="email"
              inputMode="email"
              disabled={sending}
            />
          </div>

          <button
            type="submit"
            disabled={sending}
            className="w-full rounded-full bg-[#FF914D] px-5 py-3 text-lg font-black text-white shadow-md transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {sending ? "Sending…" : "Send reset link"}
          </button>

          <div className="text-center text-sm font-bold">
            <Link
              to="/login"
              className="text-[#FF914D] transition hover:text-[#E9782F] hover:underline"
            >
              Back to login
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}